import { ApprovalInfo } from '@/types/mold';
import { UserProfile } from '@/types/user';
import { useState } from 'react';
import { useMoldOrderDetailContext } from '../../context/MoldOrderDetailContext';
import ApprovalModal from './ApprovalModal';
import ApprovalShortView from './ApprovalShortView';
import ApprovalSlot from './ApprovalSlot';
import ApprovalSummaryHeader from './ApprovalSummaryHeader';

// Helper functions for better readability
const getApprovalStatus = (approvedCount: number, totalCount: number, hasRejected: boolean) => {
    if (approvedCount === totalCount) return 'completed';
    if (hasRejected) return 'rejected';
    return 'pending';
};

const getApprovalCounts = (approverSlots: ApprovalInfo['approverSlots']) => {
    const pendingCount = approverSlots.filter(slot => slot.status === 'pending').length;
    const approvedCount = approverSlots.filter(slot => slot.status === 'approved').length;
    const completedCount = approverSlots.filter(slot => slot.status === 'approved' || slot.status === 'rejected').length;
    const totalCount = approverSlots.length;
    const hasRejected = approverSlots.some(slot => slot.status === 'rejected');
    
    return { pendingCount, approvedCount, completedCount, totalCount, hasRejected };
};

const isCurrentUserApprover = (approvalInfo: ApprovalInfo | undefined, user: UserProfile | null) => {
    if (!approvalInfo || !user) return false;

    const currentSlot = approvalInfo.approverSlots.find(slot => slot.id === approvalInfo.assignedSlotId);
    if (!currentSlot || currentSlot.status !== 'pending') return false;

    // Check if user is assigned directly
    if (currentSlot.approverType === 'user') {
        return currentSlot.assignedUser?.id === user.user_id;
    }

    // Check if user has the required role
    if (currentSlot.approverType === 'role') {
        return user.role === currentSlot.assignedRole?.role;
    }

    return false;
};

interface ApprovalListBoxProps {
    approvalInfo?: ApprovalInfo;
    user: UserProfile | null;
    onApprovalAction?: (action: 'accept' | 'reject' | 'rework', comments?: string) => void;
    displayMode?: 'default' | 'readonly';
}

const ApprovalListBox: React.FC<ApprovalListBoxProps> = ({
    approvalInfo,
    user,
    onApprovalAction,
    displayMode = 'default',
}) => {
    const { submitApproval } = useMoldOrderDetailContext();
    const currentUserCanApprove = isCurrentUserApprover(approvalInfo, user);

    const [isExpanded, setIsExpanded] = useState(currentUserCanApprove);
    const [actionType, setActionType] = useState<'accept' | 'reject' | 'rework' | null>(null);
    const [comment, setComment] = useState('');
    const [showCommentModal, setShowCommentModal] = useState(false);
    const [localError, setLocalError] = useState<string | null>(null);

    if (approvalInfo === undefined || approvalInfo.approverSlots.length === 0) {
        return null;
    }

    const counts = getApprovalCounts(approvalInfo.approverSlots);
    const approvalStatus = getApprovalStatus(counts.approvedCount, counts.totalCount, counts.hasRejected);

    // Helper function to handle approval action with comments
    const handleApprovalAction = (action: 'accept' | 'reject' | 'rework') => {
        setActionType(action);
        setLocalError(null);
        setShowCommentModal(true);
    };

    const submitApprovalAction = async () => {
        if (!actionType || !approvalInfo) return;

        try {
            setLocalError(null);
            
            // Get the current slot ID (the slot assigned to the current user)
            const currentSlot = approvalInfo.approverSlots.find(slot => slot.id === approvalInfo.assignedSlotId);
            if (!currentSlot) {
                setLocalError('ไม่พบข้อมูลผู้อนุมัติ');
                return;
            }

            // Map action types to API action types
            const actionMap = {
                'accept': 'approve' as const,
                'reject': 'reject' as const,
                'rework': 'rework' as const,
            };

            await submitApproval({
                stateInstanceId: approvalInfo.stateInstanceId,
                approverInstanceId: currentSlot.id,
                action: actionMap[actionType],
                comments: comment || undefined,
            });

            // Close modal and reset state on success
            setShowCommentModal(false);
            setComment('');
            setActionType(null);
            
            // Call the optional callback if provided
            if (onApprovalAction) {
                onApprovalAction(actionType, comment);
            }
        } catch (error) {
            // Error is already handled by the hook and stored in apiError
            console.error('Failed to submit approval:', error);
        }
    };

    const handleModalCancel = () => {
        setShowCommentModal(false);
        setComment('');
        setActionType(null);
        setLocalError(null);
    };

    // Show short version
    if (!isExpanded) {
        return (
            <ApprovalShortView
                approvalStatus={approvalStatus}
                completedCount={counts.completedCount}
                totalCount={counts.totalCount}
                onExpand={() => setIsExpanded(true)}
            />
        );
    }

    // Show full version
    return (
        <div className="space-y-3 sm:space-y-4">
            <ApprovalSummaryHeader
                approvalStatus={approvalStatus}
                completedCount={counts.completedCount}
                totalCount={counts.totalCount}
                pendingCount={counts.pendingCount}
                onCollapse={() => setIsExpanded(false)}
            />

            {/* Approval slots list */}
            <div className="space-y-2">
                {approvalInfo.approverSlots.map((slot, index) => {
                    const isCurrentSlot = slot.id === approvalInfo.assignedSlotId;
                    const isCurrentUserSlot = currentUserCanApprove && isCurrentSlot;

                    return (
                        <ApprovalSlot
                            key={slot.id}
                            slot={slot}
                            index={index}
                            isCurrentSlot={isCurrentSlot}
                            isCurrentUserSlot={isCurrentUserSlot}
                            onApprovalAction={handleApprovalAction}
                            displayMode={displayMode}
                        />
                    );
                })}
            </div>

            {/* Approval Modal */}
            <ApprovalModal
                isOpen={showCommentModal}
                actionType={actionType}
                comment={comment}
                localError={localError}
                onCommentChange={setComment}
                onCancel={handleModalCancel}
                onConfirm={submitApprovalAction}
            />
        </div>
    );
};

export default ApprovalListBox;